<script setup lang="ts">
import { computed } from "vue";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import RenewalActivityLog from "@/components/dashboard/renewals/RenewalActivityLog.vue";
import {
  CalendarDays,
  CheckCircle2,
  Clock3,
  ExternalLink,
  FileClock,
  Pencil,
  RefreshCw,
  TriangleAlert,
  XCircle,
} from "@lucide/vue";
import type { NormalizedClient } from "@/composables/useClientsQuery";
import type { RenewalTrackingDetail } from "@/composables/useRenewalTracking";

const props = defineProps<{
  client: NormalizedClient | null;
  detailClient?: RenewalTrackingDetail | null;
  isLoading?: boolean;
  isError?: boolean;
  canEdit?: boolean;
  sectionLabel?: string;
  accountStatus?: { label: string; badgeClass: string };
}>();

const emit = defineEmits<{
  (event: "open-profile"): void;
  (event: "edit"): void;
  (event: "retry"): void;
}>();

const renewalNote = computed(() =>
  String(
    props.detailClient?.renewal_note ||
      props.client?.raw?.renewal_note ||
      ""
  ).trim()
);

const renewalMilestones = computed(() => {
  const client = props.client;
  if (!client) return [];

  return [
    {
      label: "Onboarding date",
      value: formatRenewalDate(client.onboardingDateValue),
    },
    {
      label: "Subscription start",
      value: formatRenewalDate(client.subscriptionStartDateValue),
    },
    {
      label: "Renewal date",
      value: formatRenewalDate(client.renewalDateValue),
    },
    {
      label: "Payment due date",
      value: formatRenewalDate(client.paymentDueDateValue),
    },
    {
      label: "Renewal start",
      value: formatRenewalDate(client.renewalStartDateValue),
    },
    {
      label: "Renewal end",
      value: formatRenewalDate(client.renewalEndDateValue),
    },
    {
      label: props.accountStatus ? "Account status" : "Renewal status",
      value: getRenewalStatusLabel(client),
    },
  ];
});

function getRenewalStatusLabel(client: NormalizedClient | null) {
  if (props.accountStatus) return props.accountStatus.label;
  if (!client) return "Active";

  const value =
    client.raw?.renewal_status ||
    client.raw?.renewal_summary?.derived_status ||
    client.renewalStatus;

  return String(value || "Active")
    .replace(/[_-]+/g, " ")
    .trim()
    .replace(/\b\w/g, (character) => character.toUpperCase());
}

const renewalStatusLabel = computed(() => getRenewalStatusLabel(props.client));

function formatRenewalDate(value: string | null | undefined) {
  const rawValue = String(value || "").trim();
  if (!rawValue) return "mm/dd/yyyy";

  const datePart = rawValue.slice(0, 10);
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(datePart);
  if (match) return [match[2], match[3], match[1]].join("/");

  const date = new Date(rawValue);
  if (Number.isNaN(date.getTime())) return "mm/dd/yyyy";

  return date.toLocaleDateString("en-US", {
    month: "2-digit",
    day: "2-digit",
    year: "numeric",
  });
}

function statusClass(client: NormalizedClient) {
  if (props.accountStatus) return props.accountStatus.badgeClass;
  if (client.renewalStatusTone === "expired") {
    return "border-red-200 bg-red-50 text-red-700";
  }

  if (client.renewalStatusTone === "due-soon") {
    return "border-amber-200 bg-amber-50 text-amber-700";
  }

  if (client.renewalStatusTone === "renewed") {
    return "border-emerald-200 bg-emerald-50 text-emerald-700";
  }

  return "border-indigo-200 bg-indigo-50 text-indigo-700";
}

function statusIcon(client: NormalizedClient) {
  if (props.accountStatus) return Clock3;
  if (client.renewalStatusTone === "expired") return XCircle;
  if (client.renewalStatusTone === "due-soon") return TriangleAlert;
  if (client.renewalStatusTone === "renewed") return CheckCircle2;
  return Clock3;
}

function milestoneClass(value: string) {
  return value === "mm/dd/yyyy"
    ? "text-slate-400"
    : "text-slate-800";
}
</script>

<template>
  <section class="min-w-0 flex-1 bg-white">
    <div
      v-if="!client"
      class="flex h-full min-h-[28rem] flex-col items-center justify-center gap-3 px-6 text-center text-slate-400"
    >
      <FileClock
        class="size-12"
        aria-hidden="true"
      />
      <div>
        <p class="text-base font-semibold text-slate-600">
          Select an account
        </p>
        <p class="mt-1 text-sm">
          Choose an account to view its renewal details.
        </p>
      </div>
    </div>

    <div
      v-else
      class="flex h-full min-h-0 flex-col"
    >
      <header
        class="flex flex-col gap-4 border-b border-slate-200 px-5 py-5 sm:flex-row sm:items-start sm:justify-between lg:px-7"
      >
        <div class="min-w-0">
          <p class="text-xs font-bold uppercase tracking-[0.12em] text-indigo-500">
            {{ sectionLabel || "Renewal Tracking" }}
          </p>
          <div class="mt-1 flex flex-wrap items-center gap-2">
            <h1 class="truncate text-xl font-bold text-slate-800">
              {{ client.companyName }}
            </h1>
            <span class="text-sm text-slate-400">{{ client.referenceCode }}</span>
          </div>
          <p class="mt-1 truncate text-sm text-slate-500">
            {{ client.email }}
          </p>
          <p class="mt-3 max-w-xl text-sm text-slate-500">
            Update renewal milestones, payment due dates, and manual renewal notes.
          </p>
        </div>

        <div class="flex flex-wrap items-center gap-2">
          <Badge
            variant="outline"
            class="px-2.5 py-1 text-xs font-semibold"
            :class="statusClass(client)"
          >
            <component
              :is="statusIcon(client)"
              class="mr-1.5 size-3.5"
              aria-hidden="true"
            />
            {{ renewalStatusLabel }}
          </Badge>
          <Button
            v-if="canEdit"
            variant="outline"
            size="sm"
            class="border-blue-200 text-blue-700 hover:border-blue-300 hover:bg-blue-50"
            @click="emit('edit')"
          >
            Edit renewal
            <Pencil
              class="ml-2 size-3.5"
              aria-hidden="true"
            />
          </Button>
          <Button
            variant="outline"
            size="sm"
            class="border-slate-200 text-slate-600 hover:border-indigo-200 hover:bg-indigo-50 hover:text-indigo-700"
            @click="emit('open-profile')"
          >
            Full profile
            <ExternalLink
              class="ml-2 size-3.5"
              aria-hidden="true"
            />
          </Button>
        </div>
      </header>

      <div class="min-h-0 flex-1 overflow-y-auto bg-slate-50/40 p-4 lg:p-7">
        <div class="mx-auto max-w-5xl space-y-4">
          <div
            v-if="isError"
            class="flex items-center justify-between gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800"
          >
            <span>Some account details could not be loaded.</span>
            <Button
              variant="ghost"
              size="sm"
              class="text-amber-800 hover:bg-amber-100"
              @click="emit('retry')"
            >
              <RefreshCw
                class="mr-2 size-3.5"
                aria-hidden="true"
              />
              Retry
            </Button>
          </div>

          <div
            v-if="isLoading"
            class="grid gap-3 sm:grid-cols-2 lg:grid-cols-4"
          >
            <div
              v-for="item in 7"
              :key="item"
              class="h-24 animate-pulse rounded-lg bg-slate-200/70"
            />
          </div>

          <section
            v-else
            class="rounded-xl border border-slate-200 bg-white p-5 shadow-sm"
          >
            <div class="mb-4 flex items-center gap-2">
              <CalendarDays
                class="size-4 text-indigo-500"
                aria-hidden="true"
              />
              <h2 class="text-sm font-bold text-slate-700">
                Renewal milestones
              </h2>
            </div>
            <div class="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              <div
                v-for="milestone in renewalMilestones"
                :key="milestone.label"
                class="rounded-lg border border-slate-100 bg-slate-50 p-3"
              >
                <p class="text-xs font-medium text-slate-500">
                  {{ milestone.label }}
                </p>
                <p
                  class="mt-2 text-sm font-semibold"
                  :class="milestoneClass(milestone.value)"
                >
                  {{ milestone.value || "Not scheduled" }}
                </p>
              </div>
            </div>
          </section>


          <section class="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
            <div class="flex items-center gap-2">
              <FileClock
                class="size-4 text-indigo-500"
                aria-hidden="true"
              />
              <h2 class="text-sm font-bold text-slate-700">
                Renewal note
              </h2>
            </div>
            <p class="mt-3 text-sm leading-relaxed text-slate-600">
              {{ renewalNote || "No renewal note recorded." }}
            </p>
          </section>

          <RenewalActivityLog
            :history="detailClient?.renewal_history"
            :is-loading="isLoading"
            :is-unavailable="isError || !detailClient"
          />
        </div>
      </div>
    </div>
  </section>
</template>
